
import React, { useEffect, useState } from 'react';
import { TreatmentPlan, User as AppUser } from '../types';
import { getHistory } from '../services/treatmentService';
import { ArrowLeft, Users, Search, Loader2, ChevronRight, User, Calendar, BookOpen, Clock, Heart, Filter, ShieldCheck } from 'lucide-react';

interface Props {
  user: AppUser;
  onBack: () => void;
  onSelectPlan: (plan: TreatmentPlan) => void;
}

interface PatientGroup {
  name: string;
  protocols: TreatmentPlan[];
  lastVisit: string;
}

const PatientManagement: React.FC<Props> = ({ user, onBack, onSelectPlan }) => {
  const [patientGroups, setPatientGroups] = useState<PatientGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientGroup | null>(null);

  const fetchAndGroupHistory = async () => {
    setLoading(true);
    try {
      const data = await getHistory(user.id, user.profile);
      
      const groups: { [key: string]: PatientGroup } = {};
      
      data.forEach(plan => {
        const name = (plan.patientName || "Sem Nome").trim();
        const key = name.toLowerCase();
        
        if (!groups[key]) {
          groups[key] = {
            name,
            protocols: [],
            lastVisit: plan.dateCreated
          };
        }
        groups[key].protocols.push(plan);
      });

      const sortedGroups = Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
      setPatientGroups(sortedGroups);
    } catch (e) {
      console.error("Erro ao carregar fichário:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAndGroupHistory();
  }, [user.id, user.profile]);

  const filteredPatients = patientGroups.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <Loader2 className="animate-spin text-chroma-accent" size={48} />
        <p className="text-lg font-bold tracking-wide text-chroma-muted uppercase text-[10px]">Organizando Fichas de Pacientes...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in pb-20">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={selectedPatient ? () => setSelectedPatient(null) : onBack} 
            className="p-3 rounded-xl bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-200 hover:bg-purple-200 transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-chroma-text flex items-center gap-3">
              <Users size={32} className="text-chroma-accent" />
              {selectedPatient ? `Ficha: ${selectedPatient.name}` : 'Gestão de Pacientes'}
            </h2>
            <div className="flex items-center gap-2">
               <p className="text-base text-chroma-muted font-light tracking-wide">
                 {selectedPatient ? `${selectedPatient.protocols.length} protocolos realizados` : 'Fichário Individual'}
               </p>
               {user.profile === 'Master' && !selectedPatient && (
                 <span className="flex items-center gap-1 text-[10px] font-black uppercase text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                    <ShieldCheck size={10} /> Visão Global
                 </span>
               )}
            </div>
          </div>
        </div>

        {!selectedPatient && (
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-4 top-3.5 text-purple-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar por nome..."
              className="w-full bg-chroma-card border border-chroma-border rounded-xl py-3 pl-12 pr-4 text-sm focus:ring-2 focus:ring-chroma-accent/20 outline-none transition-all shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
      </div>

      {selectedPatient ? (
        <div className="grid grid-cols-1 gap-6 animate-fade-in-up">
          <div className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500/20 p-8 rounded-3xl flex items-center gap-6">
             <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center text-chroma-accent border border-white/10">
                <User size={40} />
             </div>
             <div>
                <h3 className="text-2xl font-bold text-white mb-1">{selectedPatient.name}</h3>
                <p className="text-purple-200/60 text-sm">Registro completo de sessões e evolução cromoterápica.</p>
             </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-[10px] font-black uppercase text-chroma-muted tracking-[0.3em] ml-2">Linha do Tempo de Protocolos</h4>
            {selectedPatient.protocols.map((plan) => (
              <button 
                key={plan.id}
                onClick={() => onSelectPlan(plan)}
                className="w-full bg-chroma-card border border-chroma-border p-6 rounded-2xl flex items-center justify-between hover:border-chroma-accent/50 hover:shadow-lg transition-all group text-left"
              >
                <div className="flex items-center gap-6">
                   <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-white/5 flex items-center justify-center text-chroma-muted">
                      <Clock size={24} />
                   </div>
                   <div className="flex-1">
                      <h5 className="font-bold text-lg text-chroma-text group-hover:text-chroma-accent transition-colors truncate max-w-md">
                        {plan.resumo}
                      </h5>
                      <div className="flex items-center gap-3 mt-1 text-xs text-chroma-muted font-medium">
                         <Calendar size={12} /> {plan.dateCreated}
                         <span className="px-2 py-0.5 bg-purple-100 dark:bg-white/5 rounded text-[10px] font-black uppercase">{plan.generationMethod}</span>
                      </div>
                   </div>
                </div>
                <ChevronRight className="text-purple-300 group-hover:text-chroma-accent transform group-hover:translate-x-1 transition-all" size={20} />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {patientGroups.length === 0 ? (
            <div className="bg-chroma-card border border-chroma-border rounded-3xl p-20 text-center space-y-6">
               <div className="w-20 h-20 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mx-auto text-purple-400">
                  <Users size={40} />
               </div>
               <h3 className="text-2xl font-bold text-chroma-text">Seu fichário está vazio</h3>
               <p className="text-lg font-light tracking-wide text-chroma-muted max-w-md mx-auto">
                 Inicie um novo protocolo para começar a organizar seus pacientes aqui.
               </p>
               <button onClick={fetchAndGroupHistory} className="px-8 py-3 bg-purple-600 text-white rounded-xl font-bold">Tentar Atualizar</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredPatients.map((group) => (
                <button 
                  key={group.name}
                  onClick={() => setSelectedPatient(group)}
                  className="bg-chroma-card border border-chroma-border p-8 rounded-[2rem] hover:border-chroma-accent/40 hover:shadow-xl transition-all group text-left flex flex-col gap-6"
                >
                  <div className="flex justify-between items-start">
                    <div className="w-14 h-14 rounded-2xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-chroma-accent group-hover:scale-110 transition-transform">
                      <User size={28} />
                    </div>
                    <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest border border-emerald-500/20">
                      {group.protocols.length} Sessões
                    </span>
                  </div>
                  
                  <div>
                    <h4 className="text-2xl font-bold text-chroma-text group-hover:text-chroma-accent transition-colors truncate">{group.name}</h4>
                    <p className="text-sm text-chroma-muted font-medium mt-1 flex items-center gap-2">
                       <Clock size={14} /> Último: {group.lastVisit}
                    </p>
                  </div>

                  <div className="pt-4 border-t border-purple-100 dark:border-white/5 flex items-center justify-between text-chroma-accent font-bold text-sm">
                    Ver Fichário Completo
                    <ChevronRight size={18} />
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PatientManagement;
